// @flow

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { absolute, Color, flex, hoverChild, Layout, Width } from 'styles';
import { useModalDispatch, useModalState, ModalContextActions } from 'contexts';
import { Close } from 'components/Svg';
import { ImageLoader, Link } from 'components/Gui';
import { ESC, VIDEO_PLAYER } from 'Constants';

const DesktopWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: ${Layout.Z_INDEX_MODAL};
  transform: ${({ visible }) => (visible ? `scale(1)` : `scale(0)`)};
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: ${({ visible }) => (visible ? `opacity 300ms linear` : `opacity 200ms linear, transform 0ms linear 200ms`)};
  ${flex()};
`;

const CloseButton = styled(Link)`
  z-index: 5;
  cursor: pointer;
  ${absolute('10px', '10px', 'unset', 'unset')};
  ${flex()};
  width: 32px;
  height: 32px;
  ${hoverChild('line', 'stroke', Color.BLACK_SOUL, Color.YELLOW_MELLOW)};
  display: none;

  @media print, screen and (min-width: ${Width.BREAK_MOBILE_NAV}px) {
    display: flex;
  }

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    position: absolute;
  }

  svg {
    width: 16px;
  }
`;

const Player = styled.div`
  width: 920px;
  max-width: 100vw;
  position: relative;
  ${flex('center', 'flex-start')};
  background: white;
  height: 100vh;
  overflow-y: auto;
  overflow-scrolling: touch;
  padding: 0 24px;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    height: unset;
    align-content: center;
    max-width: calc(100vw - 200px);
    padding: 80px;
  }

  > div {
    &:first-of-type {
      ${flex('center', 'center')};
      flex-direction: column;
      padding: 48px 0;

      @media print, screen and (min-width: ${Width.BREAK_MD}px) {
        ${flex('center', 'flex-start')};
        flex-direction: row;
        padding: 0;
      }

      > div {
        &:first-of-type {
          flex: 0 0 auto;
          width: 200px;
          margin-bottom: 20px;

          @media print, screen and (min-width: ${Width.BREAK_MD}px) {
            margin-right: 40px;
            margin-bottom: 0;
          }
        }

        h2 {
          font-size: 3.2rem;
          font-weight: 600;
          margin-bottom: 0;
        }

        h3 {
          font-weight: 400;
          font-style: italic;
          margin-bottom: 20px;
        }
      }
    }
  }
`;

const VideoPlayerModal = () => {
  const {
    isOpen,
    id,
    video: {
      src: { name, title, image, full },
    },
  } = useModalState();
  const dispatch = useModalDispatch();
  const closeOnClick = useCallback(() => {
    dispatch({
      type: ModalContextActions.CLOSE,
    });
  }, [dispatch]);
  const closeOnEsc = useCallback(
    ({ key, keyCode }) => {
      if (ESC.includes(key || keyCode)) {
        dispatch({
          type: ModalContextActions.CLOSE,
        });
      }
    },
    [dispatch]
  );
  const showing = isOpen && VIDEO_PLAYER === id;
  return useMemo(
    () => (
      <DesktopWrapper visible={showing} role="dialog" onKeyDown={closeOnEsc} onClick={closeOnClick}>
        {/* eslint-disable-next-line */}
        <div onClick={e => e.stopPropagation()}>
          <Player>
            <div>
              <div>{image && <ImageLoader image={image} />}</div>
              <div>
                <h2>{name}</h2>
                <h3>{title}</h3>
                <p>{full}</p>
              </div>
            </div>
            <CloseButton label="Close the modal." onClick={closeOnClick}>
              {Close}
            </CloseButton>
          </Player>
        </div>
      </DesktopWrapper>
    ),
    [showing, closeOnEsc, closeOnClick, image, name, title, full]
  );
};

export default VideoPlayerModal;
